import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import HarvestTimeAPI from './services/api';
import Login from './components/Login';
import Heatmaps from './components/Heatmaps';
import AssetBudgets from './components/AssetBudgets';
import PerformanceTrends from './components/PerformanceTrends';
import AbilityPerformance from './components/AbilityPerformance';
import QuestPerformance from './components/QuestPerformance';
import EvaluatorManager from './components/EvaluatorManager';
import { DEFAULT_PERFORMANCE_FILTERS } from './components/shared/FilterBar';
import './App.css';

function AppContent() {
  const { currentUser, logout, getToken, loading: authLoading } = useAuth();

  // Parse URL query params on mount
  const urlParams = useMemo(() => new URLSearchParams(window.location.search), []);

  const [activeTab, setActiveTab] = useState(() => {
    const fromUrl = urlParams.get('tab');
    const validTabs = ['heatmaps', 'performance-trends', 'quest-performance', 'evaluators'];
    if (validTabs.includes(fromUrl)) return fromUrl;
    const saved = localStorage.getItem('harvest-time-active-tab');
    return validTabs.includes(saved) ? saved : 'heatmaps';
  });
  const [serverStatus, setServerStatus] = useState({ checking: true, ok: false, message: '' });
  const [links, setLinks] = useState(null);
  const [buildInfo, setBuildInfo] = useState(null);

  // Heatmap filter state (lifted up to preserve across tab switches)
  const [heatmapFilters, setHeatmapFilters] = useState(() => {
    const defaults = {
      daysFilter: 7,
      customStartDate: '',
      customEndDate: '',
      colorMetric: 'framerate',
      selectedDevices: [],
      selectedQualities: [],
      selectedBranches: ['current_dev'],
      selectedCLs: []
    };
    // Apply URL params if targeting heatmaps tab
    const tabParam = urlParams.get('tab');
    if (tabParam === 'heatmaps' || !tabParam) {
      const daysParam = parseInt(urlParams.get('days'));
      if ([1, 2, 3, 7, 14, 30, 90, 365].includes(daysParam)) defaults.daysFilter = daysParam;
      const devicesParam = urlParams.get('devices');
      if (devicesParam) defaults.selectedDevices = devicesParam.split(',').map(d => d.trim()).filter(Boolean);
    }
    return defaults;
  });

  // Area to auto-expand (from URL param, one-time)
  const [initialArea] = useState(() => urlParams.get('area') || null);

  // Performance filter state (lifted up to preserve across tab switches)
  const [perfFilters, setPerfFilters] = useState(() => {
    const defaults = { ...DEFAULT_PERFORMANCE_FILTERS };
    if (urlParams.get('tab') === 'performance-trends') {
      const daysParam = parseInt(urlParams.get('days'));
      if ([1, 2, 3, 7, 14, 30, 90, 365].includes(daysParam)) defaults.daysFilter = daysParam;
    }
    return defaults;
  });
  const [abilityFilters, setAbilityFilters] = useState(() => {
    const defaults = { ...DEFAULT_PERFORMANCE_FILTERS };
    if (urlParams.get('tab') === 'ability-performance') {
      const daysParam = parseInt(urlParams.get('days'));
      if ([1, 2, 3, 7, 14, 30, 90, 365].includes(daysParam)) defaults.daysFilter = daysParam;
    }
    return defaults;
  });

  useEffect(() => {
    localStorage.setItem('harvest-time-active-tab', activeTab);
  }, [activeTab]);

  // Sync URL params whenever tab or filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (activeTab !== 'heatmaps') params.set('tab', activeTab);

    // Add filter params for the active tab
    if (activeTab === 'heatmaps') {
      if (heatmapFilters.daysFilter !== 7) params.set('days', heatmapFilters.daysFilter);
      if (heatmapFilters.selectedDevices.length > 0) params.set('devices', heatmapFilters.selectedDevices.join(','));
      if (initialArea) params.set('area', initialArea);
    } else if (activeTab === 'performance-trends') {
      if (perfFilters.daysFilter !== 7) params.set('days', perfFilters.daysFilter);
    } else if (activeTab === 'ability-performance') {
      if (abilityFilters.daysFilter !== 7) params.set('days', abilityFilters.daysFilter);
    }

    const search = params.toString();
    const newUrl = search ? `${window.location.pathname}?${search}` : window.location.pathname;
    if (window.location.search !== (search ? `?${search}` : '')) {
      window.history.replaceState({}, '', newUrl);
    }
  }, [activeTab, heatmapFilters.daysFilter, heatmapFilters.selectedDevices, perfFilters.daysFilter, abilityFilters.daysFilter, initialArea]);

  const api = useMemo(() => new HarvestTimeAPI(getToken), [getToken]);

  const checkServerHealth = useCallback(async () => {
    try {
      const health = await api.checkHealth();
      setServerStatus({
        checking: false,
        ok: health.status === 'ok',
        message: health.teamcityConfigured
          ? 'Connected to backend'
          : 'Backend running (TeamCity not configured)'
      });
      return health.status === 'ok';
    } catch (error) {
      setServerStatus({
        checking: false,
        ok: false,
        message: 'Cannot connect to backend server'
      });
      return false;
    }
  }, [api]);

  const loadLinks = useCallback(async () => {
    try {
      const result = await api.getLinks();
      setLinks(result);
    } catch {
      // Cross-linking is optional
    }
  }, [api]);

  const loadBuildInfo = useCallback(async () => {
    try {
      const info = await api.request('/build-info');
      setBuildInfo(info);
    } catch {}
  }, [api]);

  useEffect(() => {
    checkServerHealth();
    loadLinks();
    loadBuildInfo();
  }, [checkServerHealth, loadLinks, loadBuildInfo]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (err) {
      console.error('Logout failed:', err);
    }
  };

  if (authLoading) {
    return (
      <div className="app-loading">
        <div className="loading-spinner large"></div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!currentUser) {
    return <Login />;
  }

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-left">
          <h1 className="app-title">Harvest Time</h1>
          <span className="header-divider">|</span>
          <span className="header-subtitle">Measure Performance</span>
        </div>
        <div className="header-right">
          <div className={`server-status ${serverStatus.ok ? 'ok' : 'error'}`}>
            <span className="status-dot"></span>
            <span className="status-text">{serverStatus.message}</span>
          </div>
          {buildInfo && buildInfo.commit !== 'dev' && (
            <span
              className="build-indicator"
              title={`${buildInfo.commit} · ${buildInfo.branch}\n${new Date(buildInfo.deployedAt).toLocaleString()}`}
            >{buildInfo.commit}</span>
          )}
          <div className="user-info">
            <span className="user-name">{currentUser.displayName || currentUser.email}</span>
            <button className="logout-button" onClick={handleLogout}>
              Sign out
            </button>
          </div>
        </div>
      </header>

      <nav className="app-nav">
        <button
          className={`nav-tab ${activeTab === 'heatmaps' ? 'active' : ''}`}
          onClick={() => setActiveTab('heatmaps')}
        >
          Areas
        </button>
        <button
          className={`nav-tab ${activeTab === 'performance-trends' ? 'active' : ''}`}
          onClick={() => setActiveTab('performance-trends')}
        >
          Performance Trends
        </button>
        <button
          className={`nav-tab ${activeTab === 'quest-performance' ? 'active' : ''}`}
          onClick={() => setActiveTab('quest-performance')}
        >
          Quest Performance
        </button>
        <button
          className={`nav-tab ${activeTab === 'evaluators' ? 'active' : ''}`}
          onClick={() => setActiveTab('evaluators')}
        >
          Evaluators
        </button>
        {/* Hidden until test pipelines are running again
        <button
          className={`nav-tab ${activeTab === 'ability-performance' ? 'active' : ''}`}
          onClick={() => setActiveTab('ability-performance')}
        >
          Ability Performance
        </button>
        <button
          className={`nav-tab ${activeTab === 'asset-budgets' ? 'active' : ''}`}
          onClick={() => setActiveTab('asset-budgets')}
        >
          Asset Budgets
        </button>
        */}

        {links && (
          <>
            <span className="nav-divider">|</span>
            {links.harvestHealth && (
              <a href={links.harvestHealth} target="_blank" rel="noopener noreferrer" className="nav-tab external">
                HarvestHealth
              </a>
            )}
            {links.harvestRun && (
              <a href={links.harvestRun} target="_blank" rel="noopener noreferrer" className="nav-tab external">
                HarvestRun
              </a>
            )}
            {links.harvestView && (
              <a href={links.harvestView} target="_blank" rel="noopener noreferrer" className="nav-tab external">
                HarvestView
              </a>
            )}
          </>
        )}
      </nav>

      <main className="app-main">
        {activeTab === 'heatmaps' && (
          <div className="tab-content active">
            <Heatmaps api={api} filters={heatmapFilters} onFiltersChange={setHeatmapFilters} links={links} initialArea={initialArea} />
          </div>
        )}
        {activeTab === 'performance-trends' && (
          <div className="tab-content active">
            <PerformanceTrends api={api} filters={perfFilters} onFiltersChange={setPerfFilters} />
          </div>
        )}
        {activeTab === 'quest-performance' && (
          <div className="tab-content active">
            <QuestPerformance api={api} />
          </div>
        )}
        {activeTab === 'evaluators' && (
          <div className="tab-content active">
            <EvaluatorManager api={api} />
          </div>
        )}
        {/* Hidden until test pipelines are running again
        {activeTab === 'ability-performance' && (
          <div className="tab-content active">
            <AbilityPerformance api={api} filters={abilityFilters} onFiltersChange={setAbilityFilters} />
          </div>
        )}
        {activeTab === 'asset-budgets' && (
          <div className="tab-content active">
            <AssetBudgets api={api} />
          </div>
        )}
        */}
      </main>
    </div>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;
